import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, MapPin, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Bitte fülle Name, E-Mail und eine kurze Nachricht aus.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Deine Nachricht ist angekommen. Ich melde mich bald bei dir.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-cream-alt"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <span className="text-xs uppercase tracking-[0.28em] text-terracotta">
            Kontakt
          </span>
          <h2 className="mt-4 font-serif-elegant text-4xl sm:text-5xl md:text-6xl text-ocean leading-[1.05]">
            Schreib mir
            <br />
            <em className="not-italic text-terracotta">unverbindlich.</em>
          </h2>
          <p className="mt-6 text-lg text-ink/75 leading-relaxed font-light">
            Ob eine erste Frage, ein Kennenlern-Gespräch oder Interesse an einem
            meiner Werke. Ich lese jede Nachricht persönlich.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href="mailto:kerstin.cloodt@googlemail.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full bg-sage/30 flex items-center justify-center text-ocean group-hover:bg-terracotta group-hover:text-cream transition-colors">
                <Mail size={17} strokeWidth={1.5} />
              </span>
              <span className="text-ink/85 group-hover:text-terracotta transition-colors">
                kerstin.cloodt@googlemail.com
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-sage/30 flex items-center justify-center text-ocean">
                <MapPin size={17} strokeWidth={1.5} />
              </span>
              <span className="text-ink/85">Atelier &amp; Praxis in Kassel · Termine nach Vereinbarung</span>
            </div>
            <a
              href="tel:+4915168129874"
              data-testid="contact-phone-link"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full bg-sage/30 flex items-center justify-center text-ocean group-hover:bg-terracotta group-hover:text-cream transition-colors">
                <Phone size={17} strokeWidth={1.5} />
              </span>
              <span className="text-ink/85 group-hover:text-terracotta transition-colors">
                0151&nbsp;68129874
              </span>
            </a>
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-cream border border-terracotta/20">
            <p className="font-serif-elegant text-2xl text-ocean italic leading-snug">
              „Ich freue mich darauf, dich auf deinem Weg zu begleiten.&ldquo;
            </p>
            <p className="mt-2 text-sm uppercase tracking-widest text-terracotta">
              — Kerstin Cloodt
            </p>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 p-8 md:p-10 rounded-[2rem] bg-cream border border-ink/5 shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="contact-name"
                className="block text-xs uppercase tracking-[0.2em] text-ink/60 mb-2"
              >
                Dein Name
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                value={form.name}
                onChange={onChange}
                required
                data-testid="contact-input-name"
                className="w-full bg-transparent border-0 border-b border-ink/20 focus:border-terracotta outline-none py-2.5 text-ink placeholder:text-ink/40"
                placeholder="Wie darf ich dich nennen?"
              />
            </div>
            <div>
              <label
                htmlFor="contact-email"
                className="block text-xs uppercase tracking-[0.2em] text-ink/60 mb-2"
              >
                E-Mail
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                data-testid="contact-input-email"
                className="w-full bg-transparent border-0 border-b border-ink/20 focus:border-terracotta outline-none py-2.5 text-ink placeholder:text-ink/40"
                placeholder="deine@email.de"
              />
            </div>
          </div>

          <div className="mt-6">
            <label
              htmlFor="contact-subject"
              className="block text-xs uppercase tracking-[0.2em] text-ink/60 mb-2"
            >
              Betreff <span className="normal-case tracking-normal text-ink/40">(optional)</span>
            </label>
            <input
              id="contact-subject"
              name="subject"
              type="text"
              value={form.subject}
              onChange={onChange}
              data-testid="contact-input-subject"
              className="w-full bg-transparent border-0 border-b border-ink/20 focus:border-terracotta outline-none py-2.5 text-ink placeholder:text-ink/40"
              placeholder="Kennenlern-Gespräch, Werkanfrage …"
            />
          </div>

          <div className="mt-6">
            <label
              htmlFor="contact-message"
              className="block text-xs uppercase tracking-[0.2em] text-ink/60 mb-2"
            >
              Deine Nachricht
            </label>
            <textarea
              id="contact-message"
              name="message"
              rows={6}
              value={form.message}
              onChange={onChange}
              required
              data-testid="contact-input-message"
              className="w-full bg-cream-alt/70 border border-ink/10 focus:border-terracotta rounded-2xl outline-none p-4 text-ink placeholder:text-ink/40 leading-relaxed resize-none"
              placeholder="Erzähle mir, was dich bewegt, ganz ohne Erwartung."
            />
          </div>

          <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-xs text-ink/50 leading-relaxed max-w-md">
              Deine Angaben werden vertraulich behandelt und ausschließlich zur
              Beantwortung deiner Nachricht verwendet.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-terracotta text-cream text-sm tracking-wide hover:bg-terracotta-hover disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              {submitting ? "Wird gesendet…" : "Nachricht senden"}
              {!submitting && <Send size={15} />}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
